import React from 'react';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {DynamoData} from '@packages/systems/dynamo/graphql';
import {useSimulateDocumentLifecycleEvents} from '../hooks/useSimulateDocumentLifecycleEvents';

const containsAsyncDeferScript = (html: string) => {
  const scriptRegex = /<script[^>]*?(async|defer)[^>]*?>/gi;
  return scriptRegex.test(html);
};

/**
 * Detects if there are any async/defer scripts in the site level or page level custom code.
 *
 * Note: we are not doing detection in the Embeds and any Apps custom code.
 * Having async/defer scripts in the Embeds is rare, however it is possible.
 * So to make this approach more bullet proof we would need to do detection in the Embeds and Apps.
 */
const useAsyncScriptDetection = ({
  customCodeSitePostBody,
  currentPage,
  customCodeSiteHead,
}: {
  customCodeSitePostBody: string | undefined;
  currentPage: PageRecordType | undefined;
  customCodeSiteHead: string | undefined;
}) => {
  const [anyDeferAsyncScripts, setAnyDeferAsyncScripts] = React.useState<
    boolean | undefined
  >(undefined);

  const customCodePageHead = currentPage?.head;
  const customCodePagePostBody = currentPage?.postBody;

  React.useEffect(() => {
    const htmlSources = [
      customCodeSitePostBody,
      customCodePagePostBody,
      customCodeSiteHead,
      customCodePageHead,
    ];

    const hasContent = htmlSources.some(Boolean);
    const hasAsyncScripts =
      hasContent &&
      htmlSources.some((html) => html && containsAsyncDeferScript(html));

    setAnyDeferAsyncScripts(hasContent ? hasAsyncScripts : false);
  }, [
    customCodeSitePostBody,
    customCodePagePostBody,
    customCodeSiteHead,
    customCodePageHead,
  ]);

  // We store page level custom code in the currentPage object.
  // If we haven't received currentPage yet, we don't know if there are any async/defer scripts in the page level custom code
  if (!currentPage) {
    return undefined;
  }

  return anyDeferAsyncScripts;
};

type SetScriptsPending = React.Dispatch<
  React.SetStateAction<Set<string> | undefined>
>;

const createScriptObserver = (setScriptsPending: SetScriptsPending) => {
  const handleScriptLoad = (scriptSrc: string) => {
    setScriptsPending((prev) => {
      if (!prev) return new Set();
      const next = new Set(prev);
      next.delete(scriptSrc);
      return next;
    });
  };

  const observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLScriptElement) || !node.src) return;

        setScriptsPending((prev) => {
          const next = new Set(prev || []);
          next.add(node.src);
          return next;
        });
        node.addEventListener('load', () => handleScriptLoad(node.src));
        node.addEventListener('error', () => handleScriptLoad(node.src));
      });
    });
  });

  observer.observe(document, {childList: true, subtree: true});

  return () => observer.disconnect();
};

/**
 * This hook will detect if there are any async/defer scripts in the page and wait for them to be loaded.
 * We detect only async/defer scripts as any sync scripts should be inlined and executed synchronously 🤞
 */
export const useAreAllScriptsLoaded = ({
  customCodeSitePostBody,
  currentPage,
  customCodeSiteHead,
}: {
  customCodeSitePostBody: string | undefined;
  currentPage: PageRecordType | undefined;
  customCodeSiteHead: string | undefined;
}) => {
  const [scriptsPending, setScriptsPending] = React.useState<
    Set<string> | undefined
  >(undefined);
  const [areAllScriptsLoaded, setAreAllScriptsLoaded] = React.useState(false);
  const anyDeferAsyncScriptsInThePage = useAsyncScriptDetection({
    customCodeSitePostBody,
    currentPage,
    customCodeSiteHead,
  });

  // important to use useLayoutEffect and not useEffect to be able to catch mutations in document.head
  React.useLayoutEffect(() => {
    return createScriptObserver(setScriptsPending);
  }, []);

  // Unfortunately we need to explicitly set `areAllScriptsLoaded` as state as otherwise not all
  // React components/hooks are able to detect that changes to this value has happened.
  React.useLayoutEffect(() => {
    // `undefined` value indicates that we haven't made script detection yet.
    // If we haven't made script detection we don't know do we need to wait for scripts to be loaded, wait for scripts detection
    if (anyDeferAsyncScriptsInThePage === undefined) {
      setAreAllScriptsLoaded(false);
    } else {
      setAreAllScriptsLoaded(
        // If we haven't made script detection yet we don't know do we need to wait for scripts to be loaded
        scriptsPending === undefined ? false : scriptsPending.size === 0
      );
    }
  }, [scriptsPending, anyDeferAsyncScriptsInThePage]);

  return {areAllScriptsLoaded, scriptsPending};
};

type DocumentEventsProps = {
  isLoading: boolean;
  pageId: string | undefined;
  dynamoData: DynamoData | null;
  areAllScriptsLoaded: boolean;
};

/**
 * This component is used to simulate the DOMContentLoaded event that is triggered when the document has been completely loaded
 * and on each page switch. Placing this hook in the separate component allows us to access `dynamoData`
 * value and place hook call in specific place in the component tree.
 */
export const DocumentEvents = ({
  isLoading,
  pageId,
  dynamoData,
  areAllScriptsLoaded,
}: DocumentEventsProps) => {
  useSimulateDocumentLifecycleEvents(
    !isLoading && dynamoData && areAllScriptsLoaded && pageId
      ? pageId
      : undefined
  );

  return null;
};
